/**
 * MaterialUtils - helpers for three.js materials. Provides lazy-loaded debug
 * materials, default textures, and UV scaling utilities.
 */
import * as THREE from 'three';

import { UrlTexture } from './render/Texture.js';
import { isNode } from './utils/Utils.js';

export type GenericMaterialType = string | THREE.Material | THREE.Material[];
const fixedMaterials = new WeakSet<THREE.Material>();

export class MaterialUtils {
  private static defaultTexture: THREE.Texture | null = null;
  private static defaultMaterial: THREE.Material | null = null;
  private static textureColorSpace: Record<string, THREE.ColorSpace> = {
    // Base color
    'map': THREE.SRGBColorSpace,                    // Albedo / Diffuse color

    // Transparency
    'alphaMap': THREE.NoColorSpace,                 // Raw scalar mask (opacity)

    // Ambient occlusion
    'aoMap': THREE.NoColorSpace,                    // Scalar AO multiplier

    // Specular workflow
    'specularMap': THREE.NoColorSpace,              // Reflectivity intensity
    'specularColorMap': THREE.SRGBColorSpace,       // Specular tint (actual color)
    'specularIntensityMap': THREE.NoColorSpace,     // Strength scalar

    // Environment / reflections
    'envMap': THREE.LinearSRGBColorSpace,           // Radiance data (light intensity)

    // Emission
    'emissiveMap': THREE.SRGBColorSpace,            // Emissive color (treated as color)

    // Surface details
    'bumpMap': THREE.NoColorSpace,                  // Height perturbation
    'normalMap': THREE.NoColorSpace,                // XYZ vector data
    'displacementMap': THREE.NoColorSpace,          // Height scalar

    // PBR metallic-roughness workflow
    'roughnessMap': THREE.NoColorSpace,             // Scalar (0–1)
    'metalnessMap': THREE.NoColorSpace,             // Scalar (0–1)
    'anisotropyMap': THREE.NoColorSpace,            // Scalar direction strength

    // Clearcoat extension
    'clearcoatMap': THREE.NoColorSpace,             // Scalar intensity
    'clearcoatRoughnessMap': THREE.NoColorSpace,    // Scalar roughness
    'clearcoatNormalMap': THREE.NoColorSpace,       // Vector normal data

    // Iridescence extension
    'iridescenceMap': THREE.NoColorSpace,           // Scalar intensity
    'iridescenceThicknessMap': THREE.NoColorSpace,  // Scalar thickness

    // Sheen extension
    'sheenColorMap': THREE.SRGBColorSpace,          // Color tint for sheen
    'sheenRoughnessMap': THREE.NoColorSpace,        // Scalar roughness

    // Transmission / volume
    'transmissionMap': THREE.NoColorSpace,          // Scalar (light passing through)
    'thicknessMap': THREE.NoColorSpace,             // Scalar thickness depth

    // Other special maps
    'matcap': THREE.LinearSRGBColorSpace,           // Lighting baked in (light intensity)
    'gradientMap': THREE.NoColorSpace,              // Lookup indices (non-color data)
  };

  /**
   * Returns a texture that is used to display the default material.
   * @returns A texture that is used to display the default material.
   */
  public static get DefaultTexture(): THREE.Texture {
    if (!this.defaultTexture) {
      if (!isNode()) {
        this.defaultTexture = new UrlTexture({
          url: '@engine/assets/textures/T_Grid_01.texture.json',
          wrapS: THREE.RepeatWrapping,
          wrapT: THREE.RepeatWrapping,
        });
      } else {
        this.defaultTexture = new THREE.Texture();
      }
    }
    return this.defaultTexture;
  }

  /**
   * Returns a material that is used to display the default material.
   * @returns A material that is used to display the default material.
   */
  public static get DefaultMaterial(): THREE.Material {
    if (!isNode()) {
      this.defaultMaterial ??= new THREE.MeshStandardMaterial({ map: MaterialUtils.DefaultTexture, color: new THREE.Color(0.5, 0.5, 0.5) });
    } else {
      this.defaultMaterial ??= new THREE.Material();
    }
    return this.defaultMaterial;
  }

  /**
   * Disposes app-owned `scene.background` / `scene.environment` textures.
   * Call from {@link World.destroy} before nulling those fields. Pair with
   * {@link IGenesysRenderer.disposeSceneResources} (RenderObjects + background mesh
   * + node cache; this helper disposes the texture objects via the normal event path).
   */
  public static disposeSceneBackgroundAndEnvironment(scene: THREE.Scene): void {
    const bg = scene.background;
    if (bg instanceof THREE.Texture) {
      bg.dispose();
    }
    const env = scene.environment;
    if (env instanceof THREE.Texture) {
      env.dispose();
    }
  }

  /**
   * Scales the UVs of a mesh by a given factor.
   * @param mesh - The mesh to scale the UVs of.
   * @param tilesPerUnit - The number of tiles per unit.
   */
  public static scaleUVsBySize(mesh: THREE.Mesh, tilesPerUnit: number = 1): void {
    // Measure size
    const geometry = mesh.geometry;
    geometry.computeBoundingBox();
    const size = new THREE.Vector3();
    geometry.boundingBox!.getSize(size);

    const uScale = size.x > 0 ? size.x * tilesPerUnit : 1;
    const vScale = size.z > 0 ? size.z * tilesPerUnit : 1;

    const uvAttribute = geometry.attributes.uv;
    if (uvAttribute) {
      for (let i = 0; i < uvAttribute.count; i++) {
        uvAttribute.setXY(
          i,
          uvAttribute.getX(i) * uScale,
          uvAttribute.getY(i) * vScale
        );
      }
      uvAttribute.needsUpdate = true;
    }
  }

  public static fixMaterialColorSpace(material: THREE.Material): void {
    if (!material || fixedMaterials.has(material)) {
      return;
    }
    for (const key in this.textureColorSpace) {
      const texture = material[key as keyof THREE.Material];
      if (texture && texture instanceof THREE.Texture) {
        if (texture.colorSpace === THREE.NoColorSpace) {
          texture.colorSpace = this.textureColorSpace[key];
        }
      }
    }
    fixedMaterials.add(material);
  }

  public static invalidateColorSpaceFix(material: THREE.Material): void {
    if (!material) {
      return;
    }
    fixedMaterials.delete(material);
  }

  /**
   * Traverses a scene graph and fixes color spaces on all materials found.
   * @param root - The root object whose descendants will be traversed.
   */
  public static fixAllMaterials(root: THREE.Object3D): void {
    root.traverse((object: THREE.Object3D) => {
      const meshObj = object as THREE.Mesh;
      if (!meshObj.material) return;
      const materials = Array.isArray(meshObj.material) ? meshObj.material : [meshObj.material];
      for (const mat of materials) {
        if (mat) MaterialUtils.fixMaterialColorSpace(mat);
      }
    });
  }
}
